// src/pages/reports/Reports.jsx
import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  TextField,
  MenuItem,
  Grid,
  Chip,
  InputAdornment,
  Tooltip,
  Drawer,
  Divider,
  Stack,
  useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import { DataGrid } from '@mui/x-data-grid';

import Header from '../../components/Header';
import { supabase } from '../../lib/supabaseClient';

// IMPORTANT
// We assume the table name is "reports" (lowercase).
// If you created it as "Reports" in the UI, Postgres still stores it as lowercase
// (unless you quoted it). So use 'reports' in queries.

// Some setups accidentally created columns with spaces like "Report Name".
// To be resilient, we read with .select('*') and map across both styles.
// For writes, we try snake_case first and if that fails, we retry once with spaced keys.

const TABLE = 'reports';

const STATUS_OPTIONS = ['draft', 'review', 'pending', 'sent', 'completed', 'canceled', 'archived'];
const SUBSCRIPTION_OPTIONS = ['None', 'Basic', 'Plus', 'Enterprise'];

const toLocalDate = (v) => {
  if (!v) return '';
  const d = new Date(v);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const fromLocalDate = (v) => (v ? new Date(v).toISOString().slice(0, 10) : null);

// Retry helpers to support either snake_case or "Spaced Columns"
async function insertSmart(payloadSnake, payloadSpaced) {
  let resp = await supabase.from(TABLE).insert([payloadSnake]);
  if (resp.error) {
    // try again with spaced keys
    resp = await supabase.from(TABLE).insert([payloadSpaced]);
  }
  return resp;
}
async function updateSmart(id, payloadSnake, payloadSpaced) {
  let resp = await supabase.from(TABLE).update(payloadSnake).eq('id', id);
  if (resp.error) {
    resp = await supabase.from(TABLE).update(payloadSpaced).eq('id', id);
  }
  return resp;
}
async function deleteSmart(id) {
  return supabase.from(TABLE).delete().eq('id', id);
}

// ──────────────────────────────────────────────────────────────────────────────
// Add/Edit Drawer
// ──────────────────────────────────────────────────────────────────────────────
function ReportDrawer({ open, onClose, initial, onSaved, onDeleted }) {
  const editing = Boolean(initial?.id);

  const [form, setForm] = useState({
    report_name: '',
    details: '',
    ordered_by: '',
    order_date: '',
    subscription: 'None',
    status: 'draft',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({
        report_name: initial?.report_name ?? '',
        details: initial?.details ?? '',
        ordered_by: initial?.ordered_by ?? '',
        order_date: initial?.order_date ? toLocalDate(initial.order_date) : '',
        subscription: initial?.subscription ?? 'None',
        status: initial?.status ?? 'draft',
      });
      setSaving(false);
    }
  }, [open, initial]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSave = async () => {
    if (!form.report_name.trim()) {
      alert('Report name is required.');
      return;
    }
    setSaving(true);
    try {
      const payloadSnake = {
        report_name: form.report_name || null,
        details: form.details || null,
        ordered_by: form.ordered_by || null,
        order_date: fromLocalDate(form.order_date),
        subscription: form.subscription || null,
        status: form.status || null,
      };
      const payloadSpaced = {
        'Report Name': form.report_name || null,
        Details: form.details || null,
        'Ordered By': form.ordered_by || null,
        'Order Date': fromLocalDate(form.order_date),
        Subscription: form.subscription || null,
        Status: form.status || null,
      };

      if (editing) {
        const { error } = await updateSmart(initial.id, payloadSnake, payloadSpaced);
        if (error) throw error;
      } else {
        const { error } = await insertSmart(payloadSnake, payloadSpaced);
        if (error) throw error;
      }

      onSaved?.();
      onClose();
    } catch (e) {
      alert(`Save failed: ${e.message}`);
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!editing) return;
    if (!window.confirm('Delete this report? This cannot be undone.')) return;
    setSaving(true);
    try {
      const { error } = await deleteSmart(initial.id);
      if (error) throw error;
      onDeleted?.();
      onClose();
    } catch (e) {
      alert(`Delete failed: ${e.message}`);
      setSaving(false);
    }
  };

  return (
    <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: { width: 460 } }}>
      <Box display="flex" alignItems="center" justifyContent="space-between" px={2} py={1.5}>
        <Typography variant="h6">{editing ? 'Edit Report' : 'Add Report'}</Typography>
        <IconButton onClick={onClose}><CloseIcon /></IconButton>
      </Box>
      <Divider />

      <Box p={2} component="form" onSubmit={(e) => (e.preventDefault(), handleSave())}>
        <Stack spacing={2}>
          <TextField
            label="Report Name*"
            value={form.report_name}
            onChange={set('report_name')}
            fullWidth
          />
          <TextField
            label="Details"
            value={form.details}
            onChange={set('details')}
            fullWidth
            multiline
            minRows={3}
          />
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                label="Ordered By"
                value={form.ordered_by}
                onChange={set('ordered_by')}
                fullWidth
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Order Date"
                type="date"
                value={form.order_date}
                onChange={set('order_date')}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            </Grid>
          </Grid>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                select
                label="Subscription"
                value={form.subscription}
                onChange={set('subscription')}
                fullWidth
              >
                {SUBSCRIPTION_OPTIONS.map((s) => (
                  <MenuItem key={s} value={s}>{s}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField
                select
                label="Status"
                value={form.status}
                onChange={set('status')}
                fullWidth
              >
                {STATUS_OPTIONS.map((s) => (
                  <MenuItem key={s} value={s}>{s.toUpperCase()}</MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>

          <Stack direction="row" spacing={1} justifyContent="flex-end" pt={1}>
            {editing && (
              <Button
                startIcon={<DeleteOutlineIcon />}
                color="error"
                variant="outlined"
                onClick={handleDelete}
                disabled={saving}
              >
                Delete
              </Button>
            )}
            <Button startIcon={<SaveIcon />} variant="contained" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </Stack>
        </Stack>
      </Box>
    </Drawer>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// Reports Listing Page
// ──────────────────────────────────────────────────────────────────────────────
export default function Reports() {
  const theme = useTheme();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [subscriptionFilter, setSubscriptionFilter] = useState('all');

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingRow, setEditingRow] = useState(null);

  const loadRows = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from(TABLE)
        .select('*')
        .order('order_date', { ascending: false, nullsFirst: false })
        .order('id', { ascending: false });
      if (error) throw error;

      const mapped = (data || []).map((r) => ({
        id: r.id,
        report_name: r.report_name ?? r['Report Name'] ?? '',
        details: r.details ?? r['Details'] ?? '',
        ordered_by: r.ordered_by ?? r['Ordered By'] ?? '',
        order_date: r.order_date ?? r['Order Date'] ?? null,
        subscription: r.subscription ?? r['Subscription'] ?? '',
        status: r.status ?? r['Status'] ?? '',
      }));

      setRows(mapped);
    } catch (e) {
      alert(`Failed to load reports: ${e.message}`);
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRows();
  }, []);

  // Filter options
  const statusOptions = useMemo(
    () =>
      ['all', ...Array.from(new Set(rows.map((r) => r.status).filter(Boolean))).sort()],
    [rows]
  );
  const subscriptionOptions = useMemo(
    () =>
      ['all', ...Array.from(new Set(rows.map((r) => r.subscription).filter(Boolean))).sort()],
    [rows]
  );

  // Filtering + search
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (statusFilter !== 'all' && (r.status || '') !== statusFilter) return false;
      if (subscriptionFilter !== 'all' && (r.subscription || '') !== subscriptionFilter) return false;
      if (!q) return true;
      return [
        r.report_name,
        r.details,
        r.ordered_by,
        r.subscription,
        r.status,
        r.order_date ? new Date(r.order_date).toLocaleDateString() : '',
      ]
        .join(' ')
        .toLowerCase()
        .includes(q);
    });
  }, [rows, search, statusFilter, subscriptionFilter]);

  // Columns
  const columns = [
    {
      field: 'menu',
      headerName: '',
      width: 48,
      sortable: false,
      renderCell: (p) => (
        <Tooltip title="Edit">
          <IconButton
            size="small"
            onClick={() => {
              const row = rows.find((r) => r.id === p.row.id);
              setEditingRow(row || null);
              setDrawerOpen(true);
            }}
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
    { field: 'report_name', headerName: 'Report Name', flex: 1.4, minWidth: 220 },
    { field: 'ordered_by', headerName: 'Ordered By', flex: 1, minWidth: 160 },
    {
      field: 'order_date',
      headerName: 'Order Date',
      width: 140,
      valueGetter: (p) => (p.value ? new Date(p.value).toLocaleDateString() : '—'),
    },
    { field: 'subscription', headerName: 'Subscription', width: 130 },
    {
      field: 'status',
      headerName: 'Status',
      width: 130,
      renderCell: (p) => (
        <Chip
          size="small"
          label={String(p.value || '').toUpperCase()}
          color={
            String(p.value).toLowerCase() === 'completed'
              ? 'success'
              : String(p.value).toLowerCase() === 'sent'
              ? 'info'
              : String(p.value).toLowerCase() === 'canceled'
              ? 'error'
              : 'default'
          }
          variant="outlined"
        />
      ),
    },
  ];

  return (
    <Box p={3} sx={{ pb: 6 }}>
      {/* Header + actions */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Header title="Reports" subtitle="Track report definitions and orders" />
        <Box display="flex" gap={1}>
          <TextField
            size="small"
            placeholder="Search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 280 }}
          />
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={loadRows}>
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setEditingRow(null);
              setDrawerOpen(true);
            }}
          >
            New
          </Button>
        </Box>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <TextField
                select
                label="Status"
                size="small"
                fullWidth
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                {statusOptions.map((s) => (
                  <MenuItem key={s} value={s}>
                    {s === 'all' ? 'All' : s.toUpperCase()}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                select
                label="Subscription"
                size="small"
                fullWidth
                value={subscriptionFilter}
                onChange={(e) => setSubscriptionFilter(e.target.value)}
              >
                {subscriptionOptions.map((s) => (
                  <MenuItem key={s} value={s}>
                    {s === 'all' ? 'All' : s}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent>
          <Box sx={{ height: 620, width: '100%' }}>
            <DataGrid
              rows={filtered}
              columns={columns}
              loading={loading}
              disableRowSelectionOnClick
              getRowHeight={() => 52}
              onRowDoubleClick={(params) => {
                const row = rows.find((r) => r.id === params.row.id);
                setEditingRow(row || null);
                setDrawerOpen(true);
              }}
              sx={{
                '& .MuiDataGrid-columnHeaders': {
                  background: theme.palette.background.paper,
                },
              }}
            />
          </Box>
          <Box display="flex" justifyContent="space-between" alignItems="center" mt={1}>
            <Typography variant="caption" color="text.secondary">
              Reports per page: 50 • {filtered.length} results
            </Typography>
            <Typography variant="caption" color="text.secondary" />
          </Box>
        </CardContent>
      </Card>

      {/* Add/Edit Drawer */}
      <ReportDrawer
        open={drawerOpen}
        initial={editingRow}
        onClose={() => setDrawerOpen(false)}
        onSaved={loadRows}
        onDeleted={loadRows}
      />
    </Box>
  );
}
